if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      clearInterval(this.timer);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.showFeaturedMedia = false;
        /* Build color option index for alt-text filtering */
        this._colorOptionIndex = 0;
        if (window._themeProducts && this.productId) {
          const pd = window._themeProducts[this.productId];
          if (pd && pd.options) {
            const idx = pd.options.findIndex(opt => {
              const n = opt.name ? opt.name.toLowerCase() : typeof opt === 'string' ? opt.toLowerCase() : '';
              return n.includes('color') || n.includes('colour') || n.includes('màu');
            });
            if (idx !== -1) this._colorOptionIndex = idx;
          }
        }
      };

      this.initQuantityHandlers();

      if (this.currentVariant && !this.showFeaturedMedia) {
        this.updateMedia(this.currentVariant);
      }
    }

    /* No longer needed — alt text filtering replaces variantGroup JSON */

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const isColorSelection = !!target?.closest('.product-color-picker');
      const preserveViewportPosition = MinimogTheme.config.mqlMobile && isColorSelection;

      /* Mobile scroll-to-gallery — disabled */

      // INSTANT MEDIA UPDATE: Resolve color from input and filter gallery immediately
      try {
        if (this.enableVariantGroupImages) {
          const activePicker = event.target.closest('variant-picker');
          if (activePicker) {
            const colorInput = activePicker.querySelector(
              '.product-color-picker input[type="radio"]:checked'
            );
            if (colorInput) {
              this.filterGalleryByColor(colorInput.value);
            }
          }
        }
        /* Also resolve variant for price/ATC update */
        const activePicker2 = event.target.closest('variant-picker');
        if (activePicker2 && window._themeProducts && this.productId) {
          const productData = window._themeProducts[this.productId];
          if (productData && productData.variants) {
            const currentOptions = Array.from(activePicker2.querySelectorAll('input[type="radio"]:checked, select')).map(el => {
              return el.tagName === 'SELECT' ? el.options[el.selectedIndex].value : el.value;
            });
            if (currentOptions.length > 0) {
              const matchingVariant = productData.variants.find(v => {
                return v.options.length === currentOptions.length && v.options.every((opt, idx) => opt === currentOptions[idx]);
              });
              if (matchingVariant) {
                document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: matchingVariant } }));
                this.currentVariant = matchingVariant;
              }
            }
          }
        }
      } catch (err) {
        console.error("Instant media update error:", err);
      }

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        preserveViewportPosition,
        restoreFocus: !preserveViewportPosition,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode),
      });
    }

    renderProductInfo({ requestUrl, targetId, callback, preserveViewportPosition = false, restoreFocus = true }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          /* Save scroll position BEFORE DOM swap (iOS Safari workaround) */
          const scrollX = window.pageXOffset;
          const scrollY = window.pageYOffset;
          const restoreScrollPosition = () => {
            if (!preserveViewportPosition) return;

            window.scrollTo(scrollX, scrollY);
            requestAnimationFrame(() => {
              window.scrollTo(scrollX, scrollY);
            });
          };

          callback(html);

          /* Restore scroll position — iOS Safari ignores preventScroll on focus */
          restoreScrollPosition();
        })
        .then(() => {
          if (!restoreFocus) return;

          const scrollX = window.pageXOffset;
          const scrollY = window.pageYOffset;

          document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });

          /* Force restore in case iOS Safari scrolled on focus */
          window.scrollTo(scrollX, scrollY);
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.MinimogStrings.soldOut
        );
        // price update inside button
        const priceInButtonSource = html.querySelector('[data-price-in-button]');
        const priceInButtonDest = this.querySelector('[data-price-in-button]');

        if (priceInButtonDest) {
          if (priceInButtonSource && variant) {
            priceInButtonDest.innerHTML = priceInButtonSource.innerHTML;
            priceInButtonDest.style.display = '';
          } else {
            priceInButtonDest.style.display = 'none';
          }
        }


        this.currentVariant = variant;

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);
      }
    }

    setUnavailable() {
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      const addButton = productForm.querySelector('[name="add"]');

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    updateMedia(variant) {
      if (!this.productMedia) return;
      clearInterval(this.timer);

      if (this.enableVariantGroupImages) {
        /* Use alt-text color filtering instead of DOM replacement */
        const colorValue = variant.options ? variant.options[this._colorOptionIndex] : variant[`option${this._colorOptionIndex + 1}`];
        if (colorValue) {
          this.filterGalleryByColor(colorValue);
        }
      } else {
        const setActiveMedia = () => {
          if (!this.productMedia.isInitialized && typeof this.productMedia.init === 'function') {
            this.productMedia.init();
          }

          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.setActiveMedia(variant);
            return true;
          }
          return false;
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    /**
     * Alt-text color filtering: move non-matching slides OUT of the Swiper wrapper
     * into a hidden holding div, then destroy + re-init Swiper.
     * This guarantees Swiper only tracks visible slides — no index mismatch.
     */
    filterGalleryByColor(colorName) {
      if (!this.productMedia) return;
      if (this._lastFilteredColor === colorName) return;
      this._lastFilteredColor = colorName;

      const mainWrapper = this.productMedia.querySelector('.m-media-gallery__list');
      const thumbContainer = this.productMedia.querySelector('.m-product-media--slider__thumbnails .swiper-wrapper');
      if (!mainWrapper) return;

      /* 1. Create hidden holding divs (once) to park non-matching slides */
      if (!this.productMedia._holdMain) {
        this.productMedia._holdMain = document.createElement('div');
        this.productMedia._holdMain.style.display = 'none';
        this.productMedia._holdMain.className = 'gallery-hold-main';
        this.productMedia.appendChild(this.productMedia._holdMain);
      }
      if (!this.productMedia._holdThumb && thumbContainer) {
        this.productMedia._holdThumb = document.createElement('div');
        this.productMedia._holdThumb.style.display = 'none';
        this.productMedia._holdThumb.className = 'gallery-hold-thumb';
        this.productMedia.appendChild(this.productMedia._holdThumb);
      }

      /* 2. Destroy existing Swiper before moving DOM */
      if (typeof this.productMedia.destroySlider === 'function') {
        this.productMedia.destroySlider();
      }

      /* 3. Move ALL slides back from holding divs into their wrappers */
      const heldMain = this.productMedia._holdMain.querySelectorAll('.m-product-media--item');
      heldMain.forEach(slide => mainWrapper.appendChild(slide));

      if (this.productMedia._holdThumb && thumbContainer) {
        const heldThumbs = this.productMedia._holdThumb.querySelectorAll('.swiper-slide');
        heldThumbs.forEach(thumb => thumbContainer.appendChild(thumb));
      }

      /* 4. Now move NON-matching slides into the holding divs */
      const allMainSlides = mainWrapper.querySelectorAll('.m-product-media--item');
      let visibleCount = 0;
      allMainSlides.forEach(slide => {
        if (slide.dataset.color && slide.dataset.color !== colorName) {
          this.productMedia._holdMain.appendChild(slide);
        } else {
          visibleCount++;
        }
      });

      /* Fallback: if no slides match, bring them all back */
      if (visibleCount === 0) {
        const held = this.productMedia._holdMain.querySelectorAll('.m-product-media--item');
        held.forEach(slide => mainWrapper.appendChild(slide));
      }

      if (thumbContainer && this.productMedia._holdThumb) {
        const allThumbs = thumbContainer.querySelectorAll('.swiper-slide');
        let visibleThumbCount = 0;
        allThumbs.forEach(thumb => {
          if (thumb.dataset.color && thumb.dataset.color !== colorName) {
            this.productMedia._holdThumb.appendChild(thumb);
          } else {
            visibleThumbCount++;
          }
        });
        if (visibleThumbCount === 0) {
          const held = this.productMedia._holdThumb.querySelectorAll('.swiper-slide');
          held.forEach(thumb => thumbContainer.appendChild(thumb));
        }
      }

      /* 5. Re-index visible slides */
      mainWrapper.querySelectorAll('.m-product-media--item').forEach((slide, idx) => {
        slide.dataset.index = idx;
      });
      if (thumbContainer) {
        thumbContainer.querySelectorAll('.swiper-slide').forEach((thumb, idx) => {
          thumb.dataset.index = idx;
          thumb.classList.toggle('swiper-slide-thumb-active', idx === 0);
        });
      }

      /* 6. Re-init Swiper */
      if (typeof this.productMedia.initSlider === 'function') {
        this.productMedia.initSlider();
      }

      /* 7. Re-init PhotoSwipe */
      if (this.productMedia.enableImageZoom && typeof this.productMedia.handlePhotoswipe === 'function') {
        this.productMedia.handlePhotoswipe();
      }

      /* 8. Update data attribute */
      this.productMedia.dataset.selectedColor = colorName;
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${currentVariantId}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })

      // Initiate background image preloading after window loads
      this.preloadVariantImages();
    }

    preloadVariantImages() {
      /* All images are already in the DOM (hidden via CSS), so the browser
         will load them naturally. We just nudge hidden images to start
         loading their src eagerly after a delay. */
      if (!this.enableVariantGroupImages || !this.productMedia) return;
      setTimeout(() => {
        const hiddenImgs = this.productMedia.querySelectorAll('.m-color-hidden img[loading="lazy"]');
        hiddenImgs.forEach(img => { img.loading = 'eager'; });
      }, 2000);
    }
  }

  customElements.define("product-info", ProductInfo);
}
